import {
  CameraController,
  DefaultViewerParams,
  FilteringExtension,
  SectionTool,
  SelectionExtension,
  SpeckleLoader,
  UrlHelper,
  Viewer
} from "@speckle/viewer";
import {
  addIssue,
  getIssueReplies,
  getProjectIssues,
  getProjectModels,
  replyToIssue,
  setCommentDueDate,
  setCommentPriority,
  setCommentStatus,
  TOKEN
} from "@/speckleUtils";
import {Comment, toDescriptionDoc} from "@/scripts/Comment";

// A Speckle failure arrives as an errors array on an otherwise successful response; join the
// messages the way the QwebAPI views already do.
const errorMessage = (response) => response?.errors?.map((error) => error.message).join('; ')
  || "Unknown error";

// Issues require a title, but the viewer's comment box is a single body textarea:
// fall back to the first line of the body, trimmed to a sane length.
const ISSUE_TITLE_LENGTH = 80;
const toIssueTitle = (description) => {
  const firstLine = `${description ?? ''}`.split(/\r?\n/).find((line) => line.trim().length > 0)?.trim() ?? '';
  return firstLine.length > ISSUE_TITLE_LENGTH ? `${firstLine.slice(0, ISSUE_TITLE_LENGTH)}…` : firstLine;
};

const UNCATEGORIZED_CATEGORY = "Uncategorized";
const EXCLUDED_CATEGORY_KEYS = new Set([
  "<room separation>",
  "<sketch>",
  "uncategorized"
]);

function normalizeCategoryValue(rawCategory) {
  if (typeof rawCategory === "string") {
    const trimmed = rawCategory.trim();
    return trimmed.length > 0 ? trimmed : UNCATEGORIZED_CATEGORY;
  }
  if (rawCategory && typeof rawCategory === "object" && typeof rawCategory.value === "string") {
    const trimmed = rawCategory.value.trim();
    return trimmed.length > 0 ? trimmed : UNCATEGORIZED_CATEGORY;
  }
  return UNCATEGORIZED_CATEGORY;
}

function getNodeCategory(node) {
  const raw = node?.model?.raw;
  const categoryValue =
    raw?.properties?.category ??
    raw?.properties?.Category ??
    raw?.category ??
    raw?.Category;
  return normalizeCategoryValue(categoryValue);
}

function getNodeObjectId(node) {
  return node?.model?.id;
}

export const moduleViewer = {
  state    : () => ({
    viewerParams: null,
    viewer      : null,
    rightPanelTab: "comments",
    wallIsolationActive: false,
    uniqueCategories: [],
    categoryIsolation: {
      availableCategories: [],
      categoryObjectIdsMap: {},
      activeCategories: [],
      isolatedObjectIds: [],
      hiddenCategories: [],
      hiddenObjectIds: []
    },
    extensions  : {
      camera    : null,
      selection : null,
      section   : null,
      filtering : null
    },
    comments    : {
      projectId : null,
      // Which resource the loaded list is scoped to: a model id for the viewer panel,
      // or null for every issue in the project (the comments card).
      resourceId: null,
      cursor    : null,
      totalCount: null,
      // Guards against a second paging run: the comments panel and the comments view
      // both fetch on mount, and the viewer's right panel remounts on every tab switch.
      loading   : false,
      // Last failure message, surfaced by the comments card. A Speckle scope or permission
      // denial comes back HTTP 200 with an errors array, so without this it is invisible.
      error     : null,
      items     : []
    },
    prefabWalls : {
      totalCount    : 0,
      items         : [],
      wallTypes     : [],
      activeWallType: null,
    }
  }),
  getters  : {
    isCameraControlActive(state) {
      if (state.extensions === undefined) {
        return false;
      }
      return state.extensions.camera?.enabled;
    },
    isSectionToolActive(state) {
      if (state.extensions === undefined) {
        return false;
      }
      return state.extensions.section?.enabled;
    },
    isSelectionExtensionActive(state) {
      if (state.extensions === undefined) {
        return false;
      }
      return state.extensions.selection?.enabled;
    },
    getViewerRightPanelTab(state) {
      return state.rightPanelTab || "comments";
    },
    isWallIsolationActive(state) {
      return state.wallIsolationActive;
    },
    getViewerAvailableCategories(state) {
      return state.categoryIsolation.availableCategories;
    },
    getViewerUniqueCategories(state) {
      return state.uniqueCategories;
    },
    getViewerActiveCategoryIsolations(state) {
      return state.categoryIsolation.activeCategories;
    },
    getViewerHiddenCategories(state) {
      return state.categoryIsolation.hiddenCategories;
    },
    getComments(state) {
      let comments = [];
      state.comments.items.forEach((comment) => {
        comments.push(new Comment(comment));
      });
      return comments;
    },
  },
  mutations: {
    // Initializers
    initCameraController(state, payload) {
      state.extensions.camera         = payload.viewer.createExtension(CameraController);
      state.extensions.camera.enabled = true;
    },
    initSelectionExtension(state, payload) {
      state.extensions.selection         = payload.viewer.createExtension(SelectionExtension);
      state.extensions.selection.enabled = true;
    },
    initSectionTool(state, payload) {
      state.extensions.section         = payload.viewer.createExtension(SectionTool);
      state.extensions.section.enabled = false;
    },
    initFilteringExtension(state, payload) {
      state.extensions.filtering = payload.viewer.createExtension(FilteringExtension);
    },
    initComments(state, payload) {
      state.comments.projectId  = payload.projectId;
      state.comments.resourceId = payload.resourceId;
      state.comments.totalCount = payload.totalCount;
    },
    // Extension activators
    toggleCameraControl(state) {
      state.extensions.camera.enabled = true;
    },
    toggleSectionTool(state) {
      state.extensions.section.toggle();
    },
    toggleSelectionExtension(state) {
      state.extensions.selection.enabled = true;
    },
    setViewerRightPanelTab(state, payload) {
      state.rightPanelTab = payload === "filters" ? "filters" : "comments";
    },
    setWallIsolationActive(state, payload) {
      state.wallIsolationActive = Boolean(payload);
    },
    setViewerCategories(state, payload) {
      if (!Array.isArray(payload)) {
        state.categoryIsolation.availableCategories = [];
        state.uniqueCategories = [];
        return;
      }
      const sortedCategories = [...payload].sort((a, b) => a.localeCompare(b));
      state.categoryIsolation.availableCategories = sortedCategories;
      state.uniqueCategories = sortedCategories;
    },
    setViewerCategoryObjectIdsMap(state, payload) {
      state.categoryIsolation.categoryObjectIdsMap = payload && typeof payload === "object" ? payload : {};
    },
    setActiveCategoryIsolations(state, payload) {
      state.categoryIsolation.activeCategories = Array.isArray(payload) ? payload : [];
    },
    setCategoryIsolatedObjectIds(state, payload) {
      state.categoryIsolation.isolatedObjectIds = Array.isArray(payload) ? payload : [];
    },
    setHiddenCategories(state, payload) {
      state.categoryIsolation.hiddenCategories = Array.isArray(payload) ? payload : [];
    },
    setCategoryHiddenObjectIds(state, payload) {
      state.categoryIsolation.hiddenObjectIds = Array.isArray(payload) ? payload : [];
    },
    // Utility functions
    fitViewerScene(state) {
      state.extensions.camera.setCameraView(state.viewer.getRenderer().sceneBox);
    },
    fitSectionBoxToModel(state) {
      state.extensions.section.setBox(state.viewer.getRenderer().sceneBox);
    },
    selectObjects(state, payload) {
      if (!state.extensions.selection.enabled) {
        state.extensions.selection.enabled = true;
      }
      let objectIds = payload.ids;
      if (typeof objectIds == 'string') {
        objectIds = [objectIds];
      }
      state.extensions.selection.selectObjects(objectIds);
    },
    unselectObjects(state, payload) {
      if (!state.extensions.selection.enabled) {
        state.extensions.selection.enabled = true;
      }
      let objectIds = payload.ids;
      if (typeof objectIds == 'string') {
        objectIds = [objectIds];
      }
      state.extensions.selection.unselectObjects(objectIds);
    },
    // Comment functions
    // Paging can hand back a page that is already in state (the server returns a null
    // cursor past the last page, which restarts the walk), so append by id.
    addComments(state, payload) {
      const existingIds = new Set(state.comments.items.map((comment) => comment.id));
      state.comments.items.push(...payload.items.filter((item) => !existingIds.has(item.id)));
    },
    // Comments are model-scoped: selecting another model starts a new list rather than
    // appending onto the previous model's.
    resetComments(state, payload) {
      state.comments.projectId  = payload?.projectId ?? null;
      state.comments.resourceId = payload?.resourceId ?? null;
      state.comments.cursor     = null;
      state.comments.totalCount = null;
      state.comments.items      = [];
    },
    setCommentsLoading(state, payload) {
      state.comments.loading = payload;
    },
    setCommentsError(state, payload) {
      state.comments.error = payload;
    },
    setCommentsCursor(state, payload) {
      state.comments.cursor = payload;
    },
    // Replies are fetched sorted by createdAt ascending, so a new one belongs at the end.
    addReply(state, payload) {
      let comment = state.comments.items.find(c => c.id === payload.commentId);
      if (comment) {
        comment.replies.totalCount += 1;
        comment.replies.items.push(payload.reply);
      }
    },
    // A newly created issue is the most recent, and the list is sorted newest first.
    prependComment(state, payload) {
      if (state.comments.items.some((comment) => comment.id === payload.item.id)) {
        return;
      }
      state.comments.items.unshift(payload.item);
      if (state.comments.totalCount !== null) {
        state.comments.totalCount += 1;
      }
    },
    setCommentStatus(state, payload) {
      let comment = state.comments.items.find(c => c.id === payload.commentId);
      if (comment) {
        comment.status = payload.status;
      }
    },
    setCommentPriority(state, payload) {
      let comment = state.comments.items.find(c => c.id === payload.commentId);
      if (comment) {
        comment.priority = payload.priority;
      }
    },
    setCommentDueDate(state, payload) {
      let comment = state.comments.items.find(c => c.id === payload.commentId);
      if (comment) {
        comment.dueDate = payload.dueDate;
      }
    },
    setActiveWallType(state, payload) {
      state.prefabWalls.activeWallType = payload.wallType ? ("wall-type/".concat(payload.wallType)) : null;
    }
  },
  actions  : {
    async initViewer({state, commit, dispatch, rootState}, payload) {
      const token = localStorage.getItem(TOKEN);
      if (rootState.projectData && rootState.currentModel instanceof Object) {
        const urls = await UrlHelper.getResourceUrls(
          `https://app.speckle.systems/projects/${rootState.projectData.projectId}/models/${rootState.currentModel.id}`,
          token
        );

        state.viewerParams         = DefaultViewerParams;
        state.viewerParams.verbose = false;
        const viewer               = new Viewer(payload.container, state.viewerParams);
        commit('initCameraController', {viewer: viewer});
        commit('initSelectionExtension', {viewer: viewer});
        commit('initSectionTool', {viewer: viewer});
        commit('initFilteringExtension', {viewer: viewer});
        commit('setWallIsolationActive', false);

        for (const url of urls) {
          const loader = new SpeckleLoader(viewer.getWorldTree(), url, token);
          /** Load the speckle data */
          await viewer.loadObject(loader, true);
        }
        await viewer.init();
        state.viewer = viewer;
        dispatch('initializeCategoryIsolation');
        commit('fitSectionBoxToModel');
        window.viewer = state.viewer;
      } else {
        console.warn("Model viewer can't be instantiated since not project model are selected.");
      }
    },
    async fetchPrefabWallModels({state}) {
      let cursor = null;
      do {
        //TODOEB: this should not be hardcoded, at least a config
        let response = await getProjectModels("144ada10be", 10, cursor, false);
        if (state.prefabWalls.totalCount === 0) {
          state.prefabWalls.totalCount = response.data.project.models.totalCount;
        }
        cursor = response.data.project.models.cursor;
        state.prefabWalls.items.push(...response.data.project.models.items);
      } while (state.prefabWalls.items.length < state.prefabWalls.totalCount);
      state.prefabWalls.items.forEach((prefab) => {
        if (Object.hasOwn(prefab, "name") && prefab.name.startsWith("wall-type/")) {
          state.prefabWalls.wallTypes.push(prefab);
        }
      });
    },
    toggleObjectsSelection({state, commit}, payload) {
      // Selection extension must be enabled for selection to work.
      state.extensions.selection.enabled = true;
      const selectedObjectIds            = state.extensions.selection.getSelectedObjects().map(obj => obj.id);
      const removeObjects                = payload.ids.filter(obj => selectedObjectIds.includes(obj));
      const addObjects                   = payload.ids.filter(obj => !selectedObjectIds.includes(obj));
      commit('unselectObjects', {ids: removeObjects});
      commit('selectObjects', {ids: addObjects});
    },
    toggleWallsIsolation({state, commit}, payload) {
      if (!state.viewer || !state.extensions.filtering) {
        commit('setWallIsolationActive', false);
        return;
      }

      const shouldIsolate = Boolean(payload);
      const wallNodes = state.viewer.getWorldTree().findAll((node) => {
        const speckleType = node?.model?.raw?.speckle_type;
        const category = node?.model?.raw?.properties?.category || node?.model?.raw?.category;

        const hasWallSpeckleType = typeof speckleType === 'string' && speckleType.includes("RevitWall");
        const normalizedCategory = typeof category === 'string' ? category.toLowerCase() : "";
        const hasWallCategory = normalizedCategory.includes("wall") || normalizedCategory.includes("mur");
        return hasWallSpeckleType || hasWallCategory;
      });
      const wallIds = [...new Set(wallNodes
        .map((node) => node?.model?.id)
        .filter((id) => typeof id === 'string' && id.length > 0))];

      if (shouldIsolate && wallIds.length > 0) {
        state.extensions.filtering.isolateObjects(wallIds, "wall-isolation", true, true);
        commit('setWallIsolationActive', true);
        return;
      }

      if (wallIds.length > 0) {
        state.extensions.filtering.unIsolateObjects(wallIds, "wall-isolation", true, true);
      } else {
        state.extensions.filtering.resetFilters();
      }
      commit('setWallIsolationActive', false);
    },
    initializeCategoryIsolation({state, commit}) {
      if (!state.viewer) {
        commit('setViewerCategories', []);
        commit('setViewerCategoryObjectIdsMap', {});
        commit('setActiveCategoryIsolations', []);
        commit('setCategoryIsolatedObjectIds', []);
        commit('setHiddenCategories', []);
        commit('setCategoryHiddenObjectIds', []);
        return;
      }

      const categoriesMap = {};
      const categoryLabels = {};
      const allNodes = state.viewer.getWorldTree().findAll((node) => Boolean(getNodeObjectId(node)));

      allNodes.forEach((node) => {
        const objectId = getNodeObjectId(node);
        if (typeof objectId !== "string" || objectId.length === 0) {
          return;
        }
        const category = getNodeCategory(node);
        const categoryKey = category.toLowerCase();
        if (process.env.NODE_ENV === "dev") {
          console.log(categoryKey);
        }
        if (EXCLUDED_CATEGORY_KEYS.has(categoryKey)) {
          return;
        }

        if (!categoryLabels[categoryKey]) {
          categoryLabels[categoryKey] = category;
        }
        if (!categoriesMap[categoryKey]) {
          categoriesMap[categoryKey] = new Set();
        }
        categoriesMap[categoryKey].add(objectId);
      });

      const categoryObjectIdsMap = {};
      const categories = Object.keys(categoriesMap).map((key) => {
        const label = categoryLabels[key] || key;
        categoryObjectIdsMap[label] = [...categoriesMap[key]];
        return label;
      });

      commit('setViewerCategories', categories);
      commit('setViewerCategoryObjectIdsMap', categoryObjectIdsMap);
      commit('setActiveCategoryIsolations', []);
      commit('setCategoryIsolatedObjectIds', []);
      commit('setHiddenCategories', []);
      commit('setCategoryHiddenObjectIds', []);
    },
    toggleCategoryIsolation({state, commit, dispatch}, payload) {
      const category = typeof payload?.category === "string" ? payload.category : "";
      const shouldIsolate = Boolean(payload?.active);
      if (!category) {
        return;
      }

      // Hide and Isolate are mutually exclusive for a given category.
      if (shouldIsolate) {
        const hiddenCategories = [...state.categoryIsolation.hiddenCategories];
        const hiddenCategoryIndex = hiddenCategories.indexOf(category);
        if (hiddenCategoryIndex !== -1) {
          hiddenCategories.splice(hiddenCategoryIndex, 1);
          commit('setHiddenCategories', hiddenCategories);
          dispatch('applyCategoryVisibility');
        }
      }

      const activeCategories = [...state.categoryIsolation.activeCategories];
      const categoryIndex = activeCategories.indexOf(category);
      if (shouldIsolate && categoryIndex === -1) {
        activeCategories.push(category);
      }
      if (!shouldIsolate && categoryIndex !== -1) {
        activeCategories.splice(categoryIndex, 1);
      }

      commit('setActiveCategoryIsolations', activeCategories);
      dispatch('applyCategoryIsolation');
    },
    applyCategoryIsolation({state, commit}) {
      if (!state.extensions.filtering) {
        commit('setCategoryIsolatedObjectIds', []);
        return;
      }

      const previousIsolatedObjectIds = state.categoryIsolation.isolatedObjectIds;
      if (previousIsolatedObjectIds.length > 0) {
        state.extensions.filtering.unIsolateObjects(previousIsolatedObjectIds, "category-isolation", true, true);
      }

      const nextIsolatedObjectIds = [...new Set(state.categoryIsolation.activeCategories.flatMap((category) => {
        return state.categoryIsolation.categoryObjectIdsMap[category] || [];
      }))];

      if (nextIsolatedObjectIds.length > 0) {
        state.extensions.filtering.isolateObjects(nextIsolatedObjectIds, "category-isolation", true, true);
      }
      commit('setCategoryIsolatedObjectIds', nextIsolatedObjectIds);
    },
    toggleCategoryVisibility({state, commit, dispatch}, payload) {
      const category = typeof payload?.category === "string" ? payload.category : "";
      const shouldHide = Boolean(payload?.active);
      if (!category) {
        return;
      }

      // Hide and Isolate are mutually exclusive for a given category.
      if (shouldHide) {
        const activeCategories = [...state.categoryIsolation.activeCategories];
        const activeCategoryIndex = activeCategories.indexOf(category);
        if (activeCategoryIndex !== -1) {
          activeCategories.splice(activeCategoryIndex, 1);
          commit('setActiveCategoryIsolations', activeCategories);
          dispatch('applyCategoryIsolation');
        }
      }

      const hiddenCategories = [...state.categoryIsolation.hiddenCategories];
      const categoryIndex = hiddenCategories.indexOf(category);
      if (shouldHide && categoryIndex === -1) {
        hiddenCategories.push(category);
      }
      if (!shouldHide && categoryIndex !== -1) {
        hiddenCategories.splice(categoryIndex, 1);
      }

      commit('setHiddenCategories', hiddenCategories);
      dispatch('applyCategoryVisibility');
    },
    applyCategoryVisibility({state, commit}) {
      if (!state.extensions.filtering) {
        commit('setCategoryHiddenObjectIds', []);
        return;
      }

      const previousHiddenObjectIds = state.categoryIsolation.hiddenObjectIds;
      if (previousHiddenObjectIds.length > 0) {
        state.extensions.filtering.showObjects(previousHiddenObjectIds, "category-visibility", true);
      }

      const nextHiddenObjectIds = [...new Set(state.categoryIsolation.hiddenCategories.flatMap((category) => {
        return state.categoryIsolation.categoryObjectIdsMap[category] || [];
      }))];
      const isolatedObjectIds = new Set(state.categoryIsolation.isolatedObjectIds);
      const effectiveHiddenObjectIds = nextHiddenObjectIds.filter((id) => !isolatedObjectIds.has(id));
      const effectiveHiddenObjectIdSet = new Set(effectiveHiddenObjectIds);
      const effectiveHiddenCategories = state.categoryIsolation.hiddenCategories.filter((category) => {
        const categoryIds = state.categoryIsolation.categoryObjectIdsMap[category] || [];
        return categoryIds.some((id) => effectiveHiddenObjectIdSet.has(id));
      });

      if (effectiveHiddenObjectIds.length > 0) {
        state.extensions.filtering.hideObjects(effectiveHiddenObjectIds, "category-visibility", true, false);
      }
      commit('setHiddenCategories', effectiveHiddenCategories);
      commit('setCategoryHiddenObjectIds', effectiveHiddenObjectIds);
    },
    resetCategoryFilters({state, commit}) {
      if (state.extensions.filtering) {
        const hiddenObjectIds = state.categoryIsolation.hiddenObjectIds;
        if (hiddenObjectIds.length > 0) {
          state.extensions.filtering.showObjects(hiddenObjectIds, "category-visibility", true);
        }

        const isolatedObjectIds = state.categoryIsolation.isolatedObjectIds;
        if (isolatedObjectIds.length > 0) {
          state.extensions.filtering.unIsolateObjects(isolatedObjectIds, "category-isolation", true, true);
        }
      }

      commit('setHiddenCategories', []);
      commit('setCategoryHiddenObjectIds', []);
      commit('setActiveCategoryIsolations', []);
      commit('setCategoryIsolatedObjectIds', []);
    },
    // Builds the 3D reference an Issue needs to be pinned to the model: a screenshot and a
    // SerializedViewerState. Returns null when no viewer is up — the comments card has none,
    // so comments made there become general project issues instead.
    async captureViewerState({state, rootState}) {
      const camera = state.extensions.camera;
      if (!state.viewer || !camera || rootState.projectData?.projectId == null || rootState.currentModel == null) {
        return null;
      }

      const position = camera.getPosition()?.toArray();
      const target   = camera.getTarget()?.toArray();
      if (!position || !target) {
        return null;
      }

      const resourceIdString = rootState.currentModel.id;
      // Keyed by object id, which is what ModelComments reads back to re-select the object.
      const selectedObjectApplicationIds = {};
      state.extensions.selection?.getSelectedObjects?.()?.forEach((object) => {
        if (object?.id) {
          selectedObjectApplicationIds[object.id] = object.applicationId ?? null;
        }
      });

      // Hand-built rather than using @speckle/shared's formatSerializedViewerState: that
      // package is a transitive dependency of @speckle/viewer, not one of ours. The server
      // rejects the state unless projectId, resources.request.resourceIdString and the two
      // camera vectors are present; the rest are the defaults it would fill in anyway.
      const viewerState = {
        projectId: rootState.projectData.projectId,
        sessionId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        viewer   : {metadata: {filteringState: null}},
        resources: {request: {resourceIdString, threadFilters: {includeArchived: false, loadedVersionsOnly: false}}},
        ui       : {
          threads      : {openThread: {threadId: null, isTyping: false, newThreadEditor: false}},
          diff         : {command: null, time: 0, mode: 1},
          spotlightUserSessionId: null,
          filters      : {
            isolatedObjectIds: state.categoryIsolation.isolatedObjectIds ?? [],
            hiddenObjectIds  : state.categoryIsolation.hiddenObjectIds ?? [],
            selectedObjectApplicationIds,
            propertyFilters  : [],
            activeColorFilterId: null,
            filterLogic      : 'all'
          },
          camera       : {position, target, isOrthoProjection: false, zoom: 1},
          viewMode     : {mode: 0, edgesEnabled: true, edgesWeight: 1, outlineOpacity: 0.75, edgesColor: 'DEFAULT_EDGE_COLOR'},
          sectionBox   : null,
          lightConfig  : {},
          explodeFactor: 0,
          selection    : null,
          measurement  : {enabled: false, options: null, measurements: []}
        }
      };

      return {resourceIdString, viewerState, screenshot: await state.viewer.screenshot()};
    },
    // Creates an Issue: the deprecated commentMutations.create could not be used, it
    // failed on the inline description object ("Cannot convert object to primitive
    // value"). Returns the new issue id so the caller can select it.
    async addNewComment({commit, rootState}, payload) {
      const description = `${payload?.text ?? ''}`.trim();
      const title       = `${payload?.title ?? ''}`.trim() || toIssueTitle(description);
      if (!title || rootState.projectData?.projectId == null) {
        return null;
      }

      commit('setCommentsError', null);
      // Without a captured 3D reference this creates a general project issue: Speckle
      // requires resourceIdString, viewerState and screenshot together or not at all.
      const response = await addIssue(
        rootState.projectData.projectId,
        title,
        toDescriptionDoc(description),
        payload?.threeD ?? null
      );
      const issue = response?.data?.projectMutations?.issues?.createIssue;
      if (!issue) {
        console.error("Failed to create comment:", response?.errors ?? response);
        commit("setCommentsError", errorMessage(response));
        return null;
      }

      // The mutation selects the same fields as the list query, so the created issue can
      // go straight into the list instead of refetching every page.
      commit('prependComment', {item: issue});
      return issue.id;
    },
    // Comment threads are Issues server-side: the list, statuses and permissions
    // all come from the project.issues collection.
    // payload.projectWide loads every issue in the project, including "general" ones that
    // carry no 3D reference and so match no model filter. Without it the list is scoped to
    // the current model, which is what the viewer panel wants.
    async retrieveComments({state, commit, dispatch, rootState}, payload) {
      const projectWide = payload?.projectWide === true;
      // != null also guards against undefined (projectData starts as an empty object).
      if (rootState.projectData?.projectId == null || (!projectWide && rootState.currentModel == null)) {
        return;
      }
      // A run is already walking the pages; joining it would double every page.
      if (state.comments.loading) {
        return;
      }

      const projectId  = rootState.projectData.projectId;
      const resourceId = projectWide ? null : rootState.currentModel.id;

      if (state.comments.projectId !== projectId || state.comments.resourceId !== resourceId) {
        commit('resetComments', {projectId, resourceId});
      } else if (state.comments.totalCount !== null && state.comments.items.length >= state.comments.totalCount) {
        // Already loaded: a remount should not refetch the whole list.
        dispatch('retrieveCommentsReplies');
        return;
      }

      commit('setCommentsError', null);
      commit('setCommentsLoading', true);
      try {
        let cursor = state.comments.cursor;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const response = await getProjectIssues(projectId, resourceId, cursor);
          const issues   = response?.data?.project?.issues;
          if (!issues) {
            console.error("Failed to retrieve comments:", response?.errors ?? response);
            commit("setCommentsError", errorMessage(response));
            return;
          }

          // Re-read the total every page: issues can be created while we walk.
          commit('initComments', {projectId, resourceId, totalCount: issues.totalCount});
          commit('addComments', {items: issues.items});

          cursor = issues.cursor;
          commit('setCommentsCursor', cursor);

          // The server hands back a null cursor past the last page; passing it back
          // would restart at page one, which is what used to duplicate the list.
          if (!cursor || issues.items.length === 0 || state.comments.items.length >= issues.totalCount) {
            break;
          }
        }
      } finally {
        commit('setCommentsLoading', false);
      }

      dispatch('retrieveCommentsReplies');
    },
    async updateCommentStatus({state, commit, rootState}, payload) {
      const comment = state.comments.items.find(c => c.id === payload.commentId);
      if (!comment || rootState.projectData?.projectId == null) {
        return;
      }
      commit('setCommentsError', null);
      const previousStatus = comment.status ?? 'open';
      // Optimistic update, rolled back if the server rejects the change.
      commit('setCommentStatus', {commentId: payload.commentId, status: payload.status});
      const response = await setCommentStatus(rootState.projectData.projectId, payload.commentId, payload.status);
      const updated  = response?.data?.projectMutations?.issues?.updateIssue;
      if (!updated) {
        console.error("Failed to update comment status:", JSON.stringify(response?.errors ?? "Unknown error"));
        commit("setCommentsError", errorMessage(response));
        commit('setCommentStatus', {commentId: payload.commentId, status: previousStatus});
      } else {
        commit('setCommentStatus', {commentId: payload.commentId, status: updated.status});
      }
    },
    async updateCommentPriority({state, commit, rootState}, payload) {
      const comment = state.comments.items.find(c => c.id === payload.commentId);
      if (!comment || rootState.projectData?.projectId == null) {
        return;
      }
      commit('setCommentsError', null);
      const previousPriority = comment.priority ?? 'none';
      // Optimistic update, rolled back if the server rejects the change.
      commit('setCommentPriority', {commentId: payload.commentId, priority: payload.priority});
      const response = await setCommentPriority(rootState.projectData.projectId, payload.commentId, payload.priority);
      const updated  = response?.data?.projectMutations?.issues?.updateIssue;
      if (!updated) {
        console.error("Failed to update comment priority:", JSON.stringify(response?.errors ?? "Unknown error"));
        commit("setCommentsError", errorMessage(response));
        commit('setCommentPriority', {commentId: payload.commentId, priority: previousPriority});
      } else {
        commit('setCommentPriority', {commentId: payload.commentId, priority: updated.priority});
      }
    },
    async updateCommentDueDate({state, commit, rootState}, payload) {
      const comment = state.comments.items.find(c => c.id === payload.commentId);
      if (!comment || rootState.projectData?.projectId == null) {
        return;
      }
      commit('setCommentsError', null);
      const previousDueDate = comment.dueDate ?? null;
      // Optimistic update, rolled back if the server rejects the change.
      commit('setCommentDueDate', {commentId: payload.commentId, dueDate: payload.dueDate});
      const response = await setCommentDueDate(rootState.projectData.projectId, payload.commentId, payload.dueDate);
      const updated  = response?.data?.projectMutations?.issues?.updateIssue;
      if (!updated) {
        console.error("Failed to update comment due date:", JSON.stringify(response?.errors ?? "Unknown error"));
        commit("setCommentsError", errorMessage(response));
        commit('setCommentDueDate', {commentId: payload.commentId, dueDate: previousDueDate});
      } else {
        commit('setCommentDueDate', {commentId: payload.commentId, dueDate: updated.dueDate});
      }
    },
    retrieveCommentsReplies({state, commit, rootState}) {
      state.comments.items.forEach(async (comment) => {
        let cursor = null;
        if (!comment.replies.items) {
          comment.replies.items = [];
        }
        while (comment.replies.items.length < comment.replies.totalCount) {
          const response = await getIssueReplies(rootState.projectData.projectId, comment.id, cursor);
          const replies  = response?.data?.project?.issue?.replies;
          if (!replies) {
            console.error("Failed to retrieve comment replies:", response?.errors ?? response);
            commit("setCommentsError", errorMessage(response));
            return;
          }
          // Same guard as the issues walk: append by id, and stop rather than paging
          // back to the first page on a null cursor.
          const existingIds = new Set(comment.replies.items.map((reply) => reply.id));
          comment.replies.items.push(...replies.items.filter((reply) => !existingIds.has(reply.id)));
          cursor = replies.cursor;
          if (!cursor || replies.items.length === 0) {
            break;
          }
        }
      });
    },
    async addReplyToComment({commit, rootState}, payload) {
      const description = `${payload?.text ?? ''}`.trim();
      if (!description || rootState.projectData?.projectId == null) {
        return;
      }

      commit('setCommentsError', null);
      const response = await replyToIssue(
        rootState.projectData.projectId,
        payload.threadId,
        toDescriptionDoc(description)
      );
      const reply = response?.data?.projectMutations?.issues?.createReply;
      if (!reply) {
        console.error("Failed to add reply:", response?.errors ?? response);
        commit("setCommentsError", errorMessage(response));
        return;
      }

      commit('addReply', {commentId: payload.threadId, reply});
    },
  },
}
